import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/feature/MangaCard';
import HeroCarousel from './components/HeroCarousel';
import NewsletterForm from './components/NewsletterForm';
import { categories, mangas, featuredCollections } from '@/mocks/mangas';

const faqs = [
  {
    q: '樱花漫画网如何开始追一部漫画？',
    a: '进入首页或分类页，浏览你喜欢类型的漫画，点击封面即可进入详情页，再点击对应章节即可在线阅读。注册并登录后，点击收藏按钮，后续更新会在“我的收藏”中第一时间提醒你。',
  },
  {
    q: '漫画多久更新一次？',
    a: '不同漫画的更新频率不同。热门日漫新番通常每周同步更新，少女与恋爱漫画多为每周或每两周更新。你可以在漫画详情页查看上次更新时间，并订阅站内通知。',
  },
  {
    q: '能否在手机和平板上阅读？',
    a: '可以。樱花漫画网采用响应式设计，在手机、平板和电脑上都能流畅阅读。阅读时会根据屏幕宽度自动调整排版，带来舒适的移动端阅读体验。',
  },
  {
    q: '收藏后如何找到追更记录？',
    a: '登录账户后，点击页面右上角或“我的收藏”入口即可查看已收藏的漫画，并按最近更新排序。已完结与连载中的作品会分开展示，方便管理你的追更清单。',
  },
  {
    q: '遇到图片加载失败或内容问题怎么办？',
    a: '你可以通过“联系我们”页面反馈具体漫画与章节信息，我们会尽快排查修复。也可以发送邮件到 support@sakuramanga.example.com，客服会在工作日内回复。',
  },
];

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const hotMangas = [...mangas].filter((m) => m.isHot).sort((a, b) => b.rating - a.rating).slice(0, 6);
  const latestMangas = [...mangas]
    .sort((a, b) => (a.isComplete === b.isComplete ? 0 : a.isComplete ? 1 : -1))
    .filter((m) => !m.isComplete)
    .slice(0, 8);
  const completeMangas = mangas.filter((m) => m.isComplete).slice(0, 4);

  return (
    <>
      <Navbar />
      <main>
        <HeroCarousel />

        {/* 分类快捷导航 */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14" aria-label="漫画分类">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group flex items-center gap-3 rounded-xl bg-background-100 p-4 transition hover:bg-primary-100"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50 transition group-hover:bg-primary-600">
                  <i className={`${c.icon} text-xl`}></i>
                </span>
                <span>
                  <span className="block text-sm font-semibold text-foreground-900">{c.name}</span>
                  <span className="block text-xs text-foreground-500">{c.count} 部作品</span>
                </span>
              </Link>
            ))}
            <Link
              to="/category/new"
              className="group flex items-center gap-3 rounded-xl bg-accent-100 p-4 transition hover:bg-accent-200"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-500 text-background-50 transition group-hover:bg-accent-600">
                <i className="ri-flashlight-fill text-xl"></i>
              </span>
              <span>
                <span className="block text-sm font-semibold text-foreground-900">日漫新番</span>
                <span className="block text-xs text-foreground-500">火热连载中</span>
              </span>
            </Link>
          </div>
        </section>

        {/* 今日热门 */}
        <section id="hot-section" className="bg-background-100 py-10 md:py-14" aria-label="今日热门">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <span className="text-xs font-medium uppercase tracking-widest text-primary-600">HOT</span>
                <h2 className="font-heading mt-1 text-2xl font-bold text-foreground-950 md:text-3xl">今日热门</h2>
              </div>
              <Link to="/category/all" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 whitespace-nowrap transition hover:text-primary-700">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
              {hotMangas.map((m, i) => (
                <MangaCard key={m.id} manga={m} rank={i + 1} />
              ))}
            </div>
          </div>
        </section>

        {/* 最新更新 */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14" aria-label="最新更新">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-widest text-accent-600">UPDATE</span>
              <h2 className="font-heading mt-1 text-2xl font-bold text-foreground-950 md:text-3xl">最新更新</h2>
            </div>
            <Link to="/category/all" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 whitespace-nowrap transition hover:text-primary-700">
              全部更新 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {latestMangas.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        </section>

        {/* 精选专题 */}
        <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14" aria-label="精选专题">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-widest text-secondary-600">COLLECTION</span>
              <h2 className="font-heading mt-1 text-2xl font-bold text-foreground-950 md:text-3xl">精选专题</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {featuredCollections.map((col) => (
              <article key={col.id} className="group relative h-64 overflow-hidden rounded-2xl md:h-72">
                <img
                  src={col.cover}
                  alt={`${col.title} - 樱花漫画网精选专题`}
                  title={col.title}
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"></div>
                <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  {col.tag}
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <h3 className="font-heading text-xl font-bold text-background-50">{col.title}</h3>
                  <p className="mt-1 text-sm text-background-100">{col.subtitle}</p>
                  <p className="mt-2 hidden text-xs leading-5 text-background-100/80 sm:block">{col.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* 完结佳作 */}
        <section className="bg-background-100 py-10 md:py-14" aria-label="完结佳作">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <span className="text-xs font-medium uppercase tracking-widest text-primary-600">COMPLETE</span>
                <h2 className="font-heading mt-1 text-2xl font-bold text-foreground-950 md:text-3xl">一口气读完的完结佳作</h2>
              </div>
              <Link to="/category/all?status=complete" className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 whitespace-nowrap transition hover:text-primary-700">
                更多完结 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {completeMangas.map((m) => (
                <MangaCard key={m.id} manga={m} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14" aria-label="常见问题">
          <div className="mb-6 text-center">
            <span className="text-xs font-medium uppercase tracking-widest text-accent-600">FAQ</span>
            <h2 className="font-heading mt-1 text-2xl font-bold text-foreground-950 md:text-3xl">追更阅读常见问题</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((item, i) => (
              <div key={i} className="overflow-hidden rounded-xl bg-background-100">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition hover:bg-background-200"
                >
                  <span className="text-sm font-semibold text-foreground-900 md:text-base">{item.q}</span>
                  <i className={`ri-add-line text-lg text-primary-600 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <p className="px-5 pb-5 text-sm leading-6 text-foreground-600">{item.a}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Newsletter */}
        <section className="mx-auto max-w-5xl px-4 pb-10 md:px-6 md:pb-14">
          <NewsletterForm />
        </section>
      </main>

      <Footer />

      {/* 返回顶部 */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="返回顶部"
        className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary-500 text-background-50 shadow-lg transition hover:bg-primary-600 ${
          showTop ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>

      {/* FAQPage 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        })}
      </script>
    </>
  );
}